<template>
  <div
    ref="feedLayout"
    :style="{
      '--cfl-animation-duration': `${ANIMATION_DURATION}ms`,
      '--cfl-toolbar-height': `${TOOLBAR_HEIGHT}px`,
      '--back-to-top-block-height': `${BACK_TO_TOP_BLOCK_HEIGHT}px`
    }"
    class="cfl-Layout"
  >
    <div
      :class="{
        'cfl-Toolbar-extended': showBackToGroupTitleButton
      }"
      class="cfl-Toolbar"
    >
      <CheckinsFeedToolbar
        v-model="checkinsFeedListState[FILTERS_VALUES][FILTER_PRESET]"
        :hide-filters="hideFilters"
        :saved-filters="checkinsFeedListState[SAVED_FILTERS_LIST]"
        class="cfl-Toolbar_Content"
        @update-filters="checkinsFeedListState[SAVED_FILTERS_LIST] = $event"
        @reload-filters="getCustomFiltersList()"
        @filter-applied="onFilterApplied"
        @clear-filters="onClearFilters"
        @on-filter-created="onFilterCreated"
        @on-filter-updated="onFilterUpdated"
      >
        <template #toolbar-actions>
          <CheckinsFeedNavigation
            v-if="!isEmpty(checkinsFeedListState[ALLOWED_DATES_FOR_NAVIGATION])"
            v-model="checkinsFeedListState[FILTERS_VALUES][CREATE_DATE_TO]"
            :allowed-dates="checkinsFeedListState[ALLOWED_DATES_FOR_NAVIGATION]"
            :disabled="isSomeLoadingContinues"
            :has-go-to-latest-action="isShowBackToTopButton"
            :loading="checkinsFeedListState[IS_NAVIGATION_LOADING]"
            :override-date="stickyTitle?.formattedDate || UNSELECTED_DATE"
            class="cfl-Toolbar_Navigation"
            @update:model-value="onSelectDate"
            @go-to-latest="onBackToTop"
          />
        </template>
      </CheckinsFeedToolbar>

      <transition name="fade">
        <div v-if="showBackToGroupTitleButton" class="cfl-StickyHeader">
          <AppButton
            class="cfl-StickyHeader_Button"
            icon="arrow-up-next"
            type="tertiary-next"
            @click="scrollToGroupTitle(stickyTitle.index)"
          >
            {{ stickyTitle.title }}
          </AppButton>
        </div>
      </transition>
    </div>

    <div class="cfl-Content">
      <CheckinsFeedBanner v-if="shouldShowAlertsBanner" class="cfl-Content_Banner" />

      <DynamicVirtualizationList
        ref="virtualList"
        :items="checkinsFeedListState[ITEMS]"
        :loading="isSomeLoadingContinues"
        @display-items-update="onDisplayItemsUpdate"
        @bottom-arrived="loadMoreCheckins"
        @top-arrived="removeStickyTitle"
      >
        <template #item="{ item, index }">
          <AppTitle
            v-if="item[TYPE] === ITEM_TYPES.GROUP_TITLE"
            :level="3"
            class="cfl-GroupTitle"
            disable-margin
          >
            {{ item[TITLE] }}
          </AppTitle>

          <CheckinFeedGeneralEmptyState
            v-else-if="item[TYPE] === ITEM_TYPES.GENERAL_EMPTY_STATE"
            :is-filters-applied="isCustomFilterApplied"
            @clear-filters="onClearFilters"
          />

          <CheckinsFeedEmptyStateItem
            v-else-if="item[TYPE] === ITEM_TYPES.EMPTY_STATE_ITEM"
            class="cfl-EmptyStateItem"
          />

          <div v-else-if="item[TYPE] === ITEM_TYPES.CHECKIN_PREVIEW" class="cfl-Item">
            <div
              :ref="el => editElementState.setPulsedItemReference(el, item[CHECKIN_ENTITY_KEYS.ID])"
              :class="{
                'cfl-PreviewItem-pulse':
                  editElementState.highlightRelatedCheckin &&
                  editElementState.relatedCheckinId === item[CHECKIN_ENTITY_KEYS.ID]
              }"
              class="cfl-PreviewItem"
            >
              <CheckinPreviewItem
                :actions-placement="CHECKIN_PREVIEW_ACTIONS_PLACEMENTS.AFTER_COMMENT"
                :author-variant="AUTHOR_VARIANTS.COMPACT"
                :auto-scroll-to-form-mode="ELEMENT_VISIBILITY_CHECK_MODE.VIEWPORT"
                :item
                :okr-element="item.element"
                :replies-to-show="1"
                :show-replies-connections="false"
                lazy-load-comments
                show-actions
                show-element
                show-reactions
                show-replies
                table-actions-append-to=".cfl-Content"
                @update-reactions="onUpdateReactions"
                @update-replies="
                  updateCheckinReplies({
                    item,
                    itemIndex: index,
                    replies: $event,
                    checkinsList: checkinsFeedListState[ITEMS]
                  })
                "
                @edit-element="
                  editElementState.editElement({
                    element: $event.item,
                    checkinIndex: index,
                    checkinId: item[CHECKIN_ENTITY_KEYS.ID]
                  })
                "
              />
            </div>
          </div>
        </template>
        <template #loading>
          <CheckinsFeedLoader
            :show-title="!checkinsFeedListState[ITEMS].length"
            class="cfl-LoaderItem"
          />
        </template>
      </DynamicVirtualizationList>
    </div>
  </div>

  <portal to="modal-windows">
    <ObjectiveModal
      v-if="editElementState.isElementModalOpen"
      ref="elementModal"
      :workspace-id="editElementState.elementWorkspaceId"
      source="checkin-feed"
      @close="editElementState.onElementModalClose()"
      @closed="editElementState.isElementModalOpen = false"
    />
  </portal>
</template>

<script setup>
import dayjs from 'dayjs'
import { cloneDeep, isEmpty, isEqual, isFunction, isNull, isUndefined } from 'lodash'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import CheckinApiHandler from '@/api/check-in'
import LevelApiHandler from '@/api/level'
import OkrFiltersApiHandler from '@/api/okr-filters'
import { ROUTE_NAMES } from '@/routes/route-helpers'
import {
  CHECKINS_FEED_LIMIT,
  updateCheckinCommentReactions,
  updateCheckinReplies
} from '@/utils/check-ins/check-ins'
import { parseSavedFilterValues } from '@/utils/check-ins/check-ins-feed'
import {
  AUTHOR_VARIANTS,
  CHECKIN_PREVIEW_ACTIONS_PLACEMENTS
} from '@/utils/components-configurations/checkin-field'
import { ELEMENT_PERIODS_DATE_FORMAT, UNSELECTED_DATE } from '@/utils/date'
import { ASSIGNEE_ENTITY_KEYS, CHECKIN_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { OKR_TYPE_TO_FORM_VIEW } from '@/utils/objectives'
import { getEditValueFromQuery } from '@/utils/okr-elements-forms-helpers'
import { uid } from '@/utils/uid'
import { ELEMENT_VISIBILITY_CHECK_MODE } from '@/utils/window'
import { PLUGIN_OPTIONS_KEYS } from '@root/template-options-keys'

import DynamicVirtualizationList from '@/components/DynamicVirtualizationList'
import CheckinPreviewItem from '@/components/objectives/element-check-in/CheckinPreviewItem'
import ObjectiveModal from '@/components/objectives/ObjectiveModal'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import CheckinFeedGeneralEmptyState from '@/views/check-ins/CheckinFeedGeneralEmptyState'
import CheckinsFeedBanner from '@/views/check-ins/CheckinsFeedBanner'
import CheckinsFeedEmptyStateItem from '@/views/check-ins/CheckinsFeedEmptyStateItem'
import CheckinsFeedLoader from '@/views/check-ins/CheckinsFeedLoader'
import CheckinsFeedNavigation from '@/views/check-ins/CheckinsFeedNavigation'
import CheckinsFeedToolbar from '@/views/check-ins/CheckinsFeedToolbar'

defineOptions({
  name: 'CheckInsFeedLayout'
})

const props = defineProps({
  hideFilters: {
    type: Boolean,
    default: false
  },

  hideBanner: {
    type: Boolean,
    default: false
  },

  externalFilters: {
    type: Object,
    default: null
  },

  getScrollTop: {
    type: Function,
    default: null
  },

  adjustScrollTop: {
    type: Function,
    default: null
  }
})

const emit = defineEmits({
  'elements-updated': null
})

const CHECKIN_FEED_LAYOUT_KEYS = {
  TYPE: 'type',
  TITLE: 'title',
  ID: 'id'
}

const { TYPE, TITLE, ID } = CHECKIN_FEED_LAYOUT_KEYS

const CHECKINS_FEED_LIST_STATE_KEYS = {
  OFFSET: 'offset',
  IS_ALL_ITEMS_LOADED: 'isAllItemsLoaded',
  ITEMS: 'items',
  IS_LOADING: 'isLoading',
  FILTERS_VALUES: 'filtersValues',
  IS_NAVIGATION_LOADING: 'isNavigationLoading',
  IS_FILTERS_LOADING: 'isFiltersLoading',
  ALLOWED_DATES_FOR_NAVIGATION: 'allowedDatesForNavigation',
  SAVED_FILTERS_LIST: 'savedFiltersList'
}

const FILTERS_VALUES_KEYS = {
  CREATE_DATE_TO: 'createDateTo',
  FILTER_PRESET: 'filterPreset'
}

const { CREATE_DATE_TO, FILTER_PRESET } = FILTERS_VALUES_KEYS

const {
  OFFSET,
  IS_ALL_ITEMS_LOADED,
  ITEMS,
  IS_LOADING,
  IS_NAVIGATION_LOADING,
  ALLOWED_DATES_FOR_NAVIGATION,
  FILTERS_VALUES,
  IS_FILTERS_LOADING,
  SAVED_FILTERS_LIST
} = CHECKINS_FEED_LIST_STATE_KEYS

const DEFAULT_CHECKINS_FEED_LIST_STATE = {
  [OFFSET]: 0,
  [IS_ALL_ITEMS_LOADED]: false,
  [ITEMS]: [],
  [IS_LOADING]: false,
  [IS_NAVIGATION_LOADING]: false,
  [IS_FILTERS_LOADING]: false,
  [ALLOWED_DATES_FOR_NAVIGATION]: [],
  [SAVED_FILTERS_LIST]: [],
  [FILTERS_VALUES]: {
    [CREATE_DATE_TO]: UNSELECTED_DATE,
    [FILTER_PRESET]: null
  }
}

const checkinsFeedListState = ref(cloneDeep(DEFAULT_CHECKINS_FEED_LIST_STATE))

const isSomeLoadingContinues = computed(() => {
  return (
    checkinsFeedListState.value[IS_LOADING] ||
    checkinsFeedListState.value[IS_NAVIGATION_LOADING] ||
    checkinsFeedListState.value[IS_FILTERS_LOADING]
  )
})

const processedDatesCache = new Set()
const titlesMap = new Map()

const addItemToCache = (item = {}) => {
  const { formattedDate, title, index } = item

  processedDatesCache.add(formattedDate)
  titlesMap.set(index, {
    title,
    formattedDate
  })
}

const clearProcessedDatesCache = () => {
  processedDatesCache.clear()
  titlesMap.clear()
}

onBeforeUnmount(() => {
  clearProcessedDatesCache()
})

const ITEM_TYPES = {
  GROUP_TITLE: 'group-title',
  CHECKIN_PREVIEW: 'checkin-preview',
  EMPTY_STATE_ITEM: 'empty-state-item',
  GENERAL_EMPTY_STATE: 'general-empty-state'
}

const proceedResult = ({ checkinsList = [], emptyStateItems = [], reloadAll = false } = {}) => {
  const result = []

  let indexAccumulator =
    (reloadAll ? 0 : checkinsFeedListState.value[ITEMS].length) + emptyStateItems.length

  for (const item of checkinsList) {
    const formattedDate = dayjs(item[CHECKIN_ENTITY_KEYS.CHECKIN_DATE]).format(
      ELEMENT_PERIODS_DATE_FORMAT
    )

    if (!processedDatesCache.has(formattedDate)) {
      const title = dayjs(item[CHECKIN_ENTITY_KEYS.CHECKIN_DATE]).format('dddd, MMMM DD, YYYY')

      addItemToCache({
        formattedDate,
        title,
        index: indexAccumulator
      })

      result.push({
        [ID]: uid(),
        title,
        [TYPE]: ITEM_TYPES.GROUP_TITLE
      })
      indexAccumulator += 1
    }

    result.push({
      ...item,
      [TYPE]: ITEM_TYPES.CHECKIN_PREVIEW
    })
    indexAccumulator += 1
  }

  return result
}
const EMPTY_STATE_ITEMS = [
  {
    [ID]: 'empty-state-title',
    [TITLE]: dayjs().format('MMMM, YYYY'),
    [TYPE]: ITEM_TYPES.GROUP_TITLE
  },
  {
    [ID]: 'empty-state-message',
    [TYPE]: ITEM_TYPES.EMPTY_STATE_ITEM
  }
]

const GENERAL_EMPTY_STATE = [
  {
    [ID]: 'general-empty-state',
    [TYPE]: ITEM_TYPES.GENERAL_EMPTY_STATE
  }
]

const isFiltersByDateApplied = computed(() => {
  return !isEqual(
    checkinsFeedListState.value[FILTERS_VALUES][CREATE_DATE_TO],
    DEFAULT_CHECKINS_FEED_LIST_STATE[FILTERS_VALUES][CREATE_DATE_TO]
  )
})

const selectedCustomFilter = computed(() => {
  const selectedFilterId = checkinsFeedListState.value[FILTERS_VALUES]?.[FILTER_PRESET] || null

  if (!selectedFilterId) return null

  return checkinsFeedListState.value[SAVED_FILTERS_LIST].find(
    filter => filter.id === selectedFilterId
  )
})

const isCustomFilterApplied = computed(() => Boolean(selectedCustomFilter.value))

const resolveEmptyStateItems = (checkinsList = []) => {
  if (isEmpty(checkinsList)) return GENERAL_EMPTY_STATE

  if (isFiltersByDateApplied.value) return []

  const compareFormat = 'YYYY-MM'

  const currentMonthDate = dayjs().format(compareFormat)

  const isSomeCheckinHasSameMonthDate = checkinsList.some(checkin => {
    return (
      dayjs(checkin[CHECKIN_ENTITY_KEYS.CHECKIN_DATE]).format(compareFormat) === currentMonthDate
    )
  })

  return isSomeCheckinHasSameMonthDate ? [] : EMPTY_STATE_ITEMS
}

const getLimitAndOffset = ({ reloadAll = false, reloadMode = RELOAD_MODES.SOFT } = {}) => {
  const offset = reloadAll ? 0 : checkinsFeedListState.value[OFFSET]

  if (reloadMode === RELOAD_MODES.HARD) {
    return {
      limit: CHECKINS_FEED_LIMIT,
      offset
    }
  }

  let addedEmptyStateItemsCount = 0

  if (reloadAll && checkinsFeedListState.value[ITEMS].length >= 1) {
    const [
      { id: firstItemId } = { id: 'unknown-first' },
      { id: secondItemId } = { id: 'unknown-second' }
    ] = checkinsFeedListState.value[ITEMS]

    addedEmptyStateItemsCount = [...EMPTY_STATE_ITEMS, ...GENERAL_EMPTY_STATE].reduce(
      (acc, val) => {
        return acc + Number(val.id === firstItemId || val.id === secondItemId)
      },
      addedEmptyStateItemsCount
    )
  }

  const loadedItemsCount =
    checkinsFeedListState.value[ITEMS].length - processedDatesCache.size - addedEmptyStateItemsCount

  const limit = reloadAll ? Math.max(loadedItemsCount, CHECKINS_FEED_LIMIT) : CHECKINS_FEED_LIMIT

  return {
    limit,
    offset
  }
}

const store = useStore()

const userData = computed(() => store.state.system.userData)

const getCustomFiltersList = async () => {
  const api = new OkrFiltersApiHandler()
  checkinsFeedListState.value[IS_FILTERS_LOADING] = true
  try {
    const { filterViews } = await api.getCustomFiltersForFeed({
      accountId: userData.value[ASSIGNEE_ENTITY_KEYS.USER_ACCOUNT_ID]
    })
    /**
     * @note uncomment this if we need to create the first filter automatically
     * return filterViews
     */
    checkinsFeedListState.value[SAVED_FILTERS_LIST] = filterViews
  } catch (error) {
    handleError({ error })
  } finally {
    checkinsFeedListState.value[IS_FILTERS_LOADING] = false
  }
}

const selectedWorkspaceId = computed(() => store.state.workspaces.workspaceId)

watch(
  selectedWorkspaceId,
  (newValue, oldValue) => {
    if (userData.value && newValue && `${newValue}` !== `${oldValue}`) {
      Promise.all([
        store.dispatch('okrFilters/getSavedFilters', {
          accountId: userData.value[ASSIGNEE_ENTITY_KEYS.USER_ACCOUNT_ID],
          workspaceId: newValue
        }),
        store.dispatch('customFields/getAllCustomFields')
      ])
    }
  },
  { immediate: true }
)

/*
savedFilters =
 */

/**
 * @note uncomment this if we need to create the first filter automatically
const createFirstFilter = async () => {
  const filters = {
    [BACKEND_FILTER_IDS[FILTERS_KEYS.GROUP_IDS]]: JSON.stringify([]),
    [BACKEND_FILTER_IDS[FILTERS_KEYS.ASSIGNEE_IDS]]: JSON.stringify([])
  }

  const api = new OkrFiltersApiHandler()
  checkinsFeedListState.value[IS_FILTERS_LOADING] = true

  try {
    const newFilter = await api.createCustomFilterForFeed({
      accountId: userData.value[ASSIGNEE_ENTITY_KEYS.USER_ACCOUNT_ID],
      name: 'All',
      filters
    })

    checkinsFeedListState.value[SAVED_FILTERS_LIST] = [newFilter]
  } catch (error) {
    handleError({ error })
  } finally {
    checkinsFeedListState.value[IS_FILTERS_LOADING] = false
  }
}


const resolveSavedFilters = async () => {
  const savedFilters = await getCustomFiltersList()

  if (isEmpty(savedFilters)) {
    await createFirstFilter()
  } else {
    checkinsFeedListState.value[SAVED_FILTERS_LIST] = savedFilters
  }
}
 */

const getAllowedDatesForNavigation = async () => {
  const api = new CheckinApiHandler()

  checkinsFeedListState.value[IS_NAVIGATION_LOADING] = true

  try {
    checkinsFeedListState.value[ALLOWED_DATES_FOR_NAVIGATION] =
      await api.getAllowedDatesForFeedNavigation({
        ...getFiltersPayload.value
      })
  } catch (error) {
    handleError({ error })
  } finally {
    checkinsFeedListState.value[IS_NAVIGATION_LOADING] = false
  }
}

const workspaceId = computed(() => store.state.workspaces.workspaceId)

const getLevelsForFilter = async () => {
  const levelApi = new LevelApiHandler()

  try {
    const levels = await levelApi.getLevelsForFilter({
      workspaceId: workspaceId.value
    })

    store.dispatch('objectives/setLevels', { levels })
  } catch (error) {
    handleError({ error })
  }
}

const getInitialData = async () => {
  /**
   * @note uncomment this if we need to create the first filter automatically
   await Promise.all([getAllowedDatesForNavigation(), getCheckinsList(), resolveSavedFilters()])
   */
  const requests = [getAllowedDatesForNavigation(), getCheckinsList()]
  if (!props.hideFilters) {
    requests.push(...[getCustomFiltersList(), getLevelsForFilter()])
  }

  await Promise.all(requests)
}

const RELOAD_MODES = {
  HARD: 'hard',
  SOFT: 'soft'
}

const getFiltersPayload = computed(() => {
  if (!isNull(props.externalFilters)) {
    return { ...props.externalFilters }
  }

  if (isCustomFilterApplied.value) {
    return parseSavedFilterValues({ filterData: selectedCustomFilter.value })
  }

  return {}
})

const getCheckinsList = async ({ reloadAll = false, reloadMode = RELOAD_MODES.SOFT } = {}) => {
  const api = new CheckinApiHandler()

  if (reloadAll && reloadMode === RELOAD_MODES.HARD) {
    checkinsFeedListState.value[OFFSET] = 0
    checkinsFeedListState.value[ITEMS] = []
    checkinsFeedListState.value[IS_ALL_ITEMS_LOADED] = false
  }

  checkinsFeedListState.value[IS_LOADING] = true

  const { limit, offset } = getLimitAndOffset({ reloadAll, reloadMode })

  try {
    const result = await api.getCheckinsForFeed({
      limit,
      offset,
      ...checkinsFeedListState.value[FILTERS_VALUES],
      ...getFiltersPayload.value
    })

    if (result.length < limit) {
      checkinsFeedListState.value[IS_ALL_ITEMS_LOADED] = true
    }

    let resolvedEmptyStateItems = []

    if (reloadAll || isEmpty(checkinsFeedListState.value[ITEMS])) {
      resolvedEmptyStateItems = resolveEmptyStateItems(result)
      clearProcessedDatesCache()
    }

    const enrichedResult = proceedResult({
      checkinsList: result,
      emptyStateItems: resolvedEmptyStateItems,
      reloadAll
    })

    if (reloadAll) {
      checkinsFeedListState.value[ITEMS] = enrichedResult
    } else {
      checkinsFeedListState.value[ITEMS].push(...enrichedResult)
    }

    if (!isEmpty(resolvedEmptyStateItems)) {
      checkinsFeedListState.value[ITEMS].unshift(...resolvedEmptyStateItems)
    }
  } catch (error) {
    handleError({ error })
  } finally {
    checkinsFeedListState.value[IS_LOADING] = false
  }
}

const loadMoreCheckins = async () => {
  if (checkinsFeedListState.value[IS_LOADING] || checkinsFeedListState.value[IS_ALL_ITEMS_LOADED]) {
    return
  }

  checkinsFeedListState.value[OFFSET] += CHECKINS_FEED_LIMIT

  await getCheckinsList()
}

const router = useRouter()
const checkinFeedForUsersVisibility = computed(
  () => store.getters['system/checkinFeedForUsersVisibility']
)
const removeStickyTitle = () => {
  stickyTitle.value = null
}

const clearSelectedDate = () => {
  checkinsFeedListState.value[FILTERS_VALUES][CREATE_DATE_TO] =
    DEFAULT_CHECKINS_FEED_LIST_STATE[FILTERS_VALUES][CREATE_DATE_TO]
}

const onSelectDate = async () => {
  removeStickyTitle()
  clearProcessedDatesCache()

  await getCheckinsList({ reloadAll: true, reloadMode: RELOAD_MODES.HARD })
}

const onBackToTop = async () => {
  removeStickyTitle()
  clearProcessedDatesCache()
  clearSelectedDate()

  await getCheckinsList({ reloadAll: true, reloadMode: RELOAD_MODES.HARD })
}

const onFilterApplied = async () => {
  removeStickyTitle()
  clearProcessedDatesCache()
  clearSelectedDate()

  await Promise.all([
    getAllowedDatesForNavigation(),
    getCheckinsList({ reloadAll: true, reloadMode: RELOAD_MODES.HARD })
  ])
}

const onFilterCreated = async newFilter => {
  await getCustomFiltersList()
  const { id } = newFilter
  checkinsFeedListState.value[FILTERS_VALUES][FILTER_PRESET] = id
  await onFilterApplied()
}

const onFilterUpdated = async updatedFilter => {
  checkinsFeedListState.value[SAVED_FILTERS_LIST] = checkinsFeedListState.value[
    SAVED_FILTERS_LIST
  ].map(filter => {
    if (filter.id === updatedFilter.id) {
      const { filters, name } = updatedFilter
      return { ...filter, filters, name }
    }
    return filter
  })

  const isUpdatedFilterSelected =
    checkinsFeedListState.value[FILTERS_VALUES][FILTER_PRESET] === updatedFilter.id

  if (isUpdatedFilterSelected) {
    await onFilterApplied()
  }
}

const onClearFilters = async () => {
  checkinsFeedListState.value[FILTERS_VALUES][FILTER_PRESET] = null
  await onFilterApplied()
}

const route = useRoute()

const handleElementForEditFromQuery = () => {
  if (route) {
    const editValue = getEditValueFromQuery({ query: route.query })

    if (editValue) {
      editElementState.value.openElementFormFromQuery({ element: editValue })

      const { children } = editValue

      if (children) {
        editElementState.value.openElementFormFromQuery({
          element: children,
          isChildren: true
        })
      }
    }
  }
}

onMounted(() => {
  if (!checkinFeedForUsersVisibility.value) {
    router.push({ name: ROUTE_NAMES.MISSING_PAGE })
    return
  }
  getInitialData()

  handleElementForEditFromQuery()

  store.dispatch('dashboard/getFavoriteList')
})

const ANIMATION_DURATION = 1500
const virtualListReference = useTemplateRef('virtualList')

const TRACKING_SOURCE = 'checkin feed'

const editElementState = ref({
  relatedCheckinId: null,
  checkinIndexInList: null,
  elementModalReference: useTemplateRef('elementModal'),
  elementWorkspaceId: null,
  isElementModalOpen: false,
  highlightRelatedCheckin: false,
  highlightedItemReference: null,
  timeoutId: undefined,

  openElementFormFromQuery: function ({ element = {}, isChildren = false } = {}) {
    this.elementWorkspaceId = element.workspaceId || null
    this.isElementModalOpen = true
    nextTick(() => {
      const openMethod = isChildren
        ? this.elementModalReference.openChildForm
        : this.elementModalReference.openForm

      openMethod(OKR_TYPE_TO_FORM_VIEW[element.typeId], { ...element }, TRACKING_SOURCE, true)
    })
  },

  editElement: function ({ element, checkinIndex, checkinId } = {}) {
    this.elementWorkspaceId = element.workspaceId
    this.relatedCheckinId = checkinId
    this.checkinIndexInList = checkinIndex
    this.highlightRelatedCheckin = false
    this.isElementModalOpen = true
    clearTimeout(this.timeoutId)

    nextTick(() => {
      this.elementModalReference.openForm(
        OKR_TYPE_TO_FORM_VIEW[element.typeId],
        { ...element },
        TRACKING_SOURCE,
        true
      )
    })
  },

  onElementModalClose: async function () {
    const oldCheckinIndex = this.checkinIndexInList
    const checkinId = this.relatedCheckinId
    this.elementWorkspaceId = null
    this.checkinIndexInList = null

    emit('elements-updated')

    await getCheckinsList({ reloadAll: true })

    let resolvedElementIndex = oldCheckinIndex

    const isRelatedCheckinInTheSamePosition =
      checkinsFeedListState.value[ITEMS][oldCheckinIndex]?.[CHECKIN_ENTITY_KEYS.ID] === checkinId

    if (!isRelatedCheckinInTheSamePosition) {
      resolvedElementIndex = checkinsFeedListState.value[ITEMS].findIndex(
        item => item[CHECKIN_ENTITY_KEYS.ID] === checkinId
      )
    }

    if (resolvedElementIndex !== -1) {
      this.highlightRelatedCheckin = true
      virtualListReference.value
        .scrollToItemByIndex({
          index: resolvedElementIndex,
          makeSecondAttempt: false
        })
        .then(() => {
          if (this.highlightedItemReference) {
            this.highlightedItemReference.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })
          }
        })

      this.timeoutId = setTimeout(() => {
        this.relatedCheckinId = null
        this.highlightRelatedCheckin = false
      }, ANIMATION_DURATION * 3)
    } else {
      this.relatedCheckinId = null
    }
  },

  setPulsedItemReference: function (el, checkinId) {
    if (el && this.highlightRelatedCheckin && this.relatedCheckinId === checkinId) {
      this.highlightedItemReference = el
    }
  }
})

const stickyTitle = ref(null)
const feedLayoutRef = useTemplateRef('feedLayout')

const BACK_TO_TOP_BLOCK_HEIGHT = 50
const TOOLBAR_HEIGHT = props.hideFilters ? 48 : 80
const AVAILABLE_OFFSET_TOP = BACK_TO_TOP_BLOCK_HEIGHT + TOOLBAR_HEIGHT

const currentTopElementIndex = ref(0)

const isShowBackToTopButton = computed(() => {
  return isFiltersByDateApplied.value && !!currentTopElementIndex.value
})

const showBackToGroupTitleButton = computed(() => {
  return !isNull(stickyTitle.value) && !!currentTopElementIndex.value
})

const getFeedLayoutContainer = () => {
  return feedLayoutRef.value
}

const resolveFeedScrollTop = () => {
  if (isFunction(props.getScrollTop)) return props.getScrollTop()

  return feedLayoutRef.value.scrollTop
}

const onDisplayItemsUpdate = ({ visibleStartIndex } = {}) => {
  currentTopElementIndex.value = Number(visibleStartIndex)
  if (resolveFeedScrollTop() < AVAILABLE_OFFSET_TOP || isUndefined(visibleStartIndex)) {
    removeStickyTitle()
    return
  }

  const titlesIndices = Array.from(titlesMap.keys())

  const indexOfCurrentTitle = titlesIndices.findLast(i => i <= visibleStartIndex)

  const titleObject = titlesMap.get(indexOfCurrentTitle) ?? null

  stickyTitle.value = titleObject
    ? {
        index: indexOfCurrentTitle,
        ...titleObject
      }
    : null
}

const scrollToGroupTitle = async index => {
  virtualListReference.value
    .scrollToItemByIndex({
      index: index,
      secondAttemptTimeout: 200
    })
    .then(() => {
      nextTick(() => {
        if (isFunction(props.adjustScrollTop)) {
          props.adjustScrollTop(AVAILABLE_OFFSET_TOP)
          return
        }

        feedLayoutRef.value.scrollTop = Math.max(
          0,
          feedLayoutRef.value.scrollTop - AVAILABLE_OFFSET_TOP
        )
      })
    })
}

const onUpdateReactions = ({ checkinId, commentId, parentId, reactions }) => {
  updateCheckinCommentReactions({
    checkinsList: checkinsFeedListState.value[ITEMS],
    checkinId,
    commentId,
    parentId,
    reactions
  })
}

const isPluginAdmin = computed(() => store.state.pluginOptions[PLUGIN_OPTIONS_KEYS.IS_PLUGIN_ADMIN])

const showAlertsBannerSavedStatus = computed(() => store.getters['system/isShowBannerOnFeed'])

const shouldShowAlertsBanner = computed(() => {
  if (props.hideBanner) return false

  return isPluginAdmin.value && showAlertsBannerSavedStatus.value
})

watch(
  () => props.externalFilters,
  newValue => {
    if (isNull(newValue)) return

    onFilterApplied()
  },
  { deep: true }
)

defineExpose({
  getFeedLayoutContainer
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/mixins';
@import '~@/assets/styles/canvas-dimensions';
@import '~@/assets/styles/checkins-feed';

.cfl-Layout {
  height: var(--ob-cfl-height, #{$default-canvas-height-non-objectives-page});
  background-color: var(--ob-cfl-background, var(--gray-0));
  @include styled-native-scrollbar();
  overflow-y: var(--ob-cfl-overflow-y, auto);
  padding: 0 $page-right-padding 36px $page-left-padding;
  --checkin-layout-row-gap: 0;
  --checkin-layout-body-padding: 0 #{$checkin-feed-item-px} #{$checkin-feed-item-py} #{$checkin-feed-item-px};
  --checkin-layout-author-padding: 18px #{$checkin-feed-item-px};
  --checkin-layout-pl: #{$checkin-feed-item-px};
  --checkin-layout-pr: #{$checkin-feed-item-px};
  --other-replies-button-offset-y: 24px;
  --feed-max-width: 880px;
}

.cfl-Toolbar {
  min-width: var(--feed-max-width);
  height: var(--cfl-toolbar-height);
  position: sticky;
  top: 0;
  z-index: 3;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;

    background: linear-gradient(
      180deg,
      var(--ob-cfl-toolbar-fade, var(--gray-0)) 55%,
      transparent 100%
    );

    transition: height $menu-transition;
    z-index: -1;
    pointer-events: none;
  }

  &-extended {
    &:before {
      height: calc(100% + var(--back-to-top-block-height));
    }
  }
}

.cfl-Toolbar_Content {
  width: calc(var(--feed-max-width) - 40px);
  margin-inline: auto;
}

.cfl-Toolbar_Navigation {
  margin-left: auto;
}

.cfl-Content {
  width: var(--feed-max-width);
  margin-inline: auto;
  z-index: 1;
  position: relative;
}

.cfl-Item {
  position: relative; // do not remove
  // overflow: hidden;

  &:not(&-last) {
    padding-bottom: $checkins-feed-items-gap;
  }
}

.cfl-Item,
.cfl-GroupTitle,
.cfl-EmptyStateItem,
.cfl-LoaderItem {
  padding-inline: 20px;
}

.cfl-PreviewItem {
  // padding: 20px 32px 24px;
  @extend %feed-item-common-styles;

  &-pulse {
    animation-delay: 0.5s;
    animation: pulse var(--cfl-animation-duration) ease-in-out infinite,
      scaling var(--cfl-animation-duration) ease-in-out;
  }
}

@keyframes scaling {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.01);
  }
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 transparent;
  }
  50% {
    border-color: var(--grade-high-color-next-100);
    box-shadow: 0 0 0 6px var(--grade-high-color-next-0);
  }
}

.cfl-GroupTitle {
  padding-bottom: $checkins-feed-items-gap;
  font-size: $fs-18;
  line-height: 28px;
}

.cfl-StickyHeader {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 2;
  height: var(--back-to-top-block-height);
  width: 100%;
  transform: translateY(100%);
  margin-inline: auto;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 14px;
}

.cfl-StickyHeader_Button {
  background-color: $grey-3-next;
  box-shadow: 0 0 25px 0 rgba(0, 0, 0, 0.15);
  font-size: $fs-12;
  line-height: 16px;
  font-weight: fw('bold');

  @include hoverState($grey-3-next, 1%);
}

.cfl-Content_Banner {
  width: calc(var(--feed-max-width) - 40px);
  margin: 0 auto $checkins-feed-items-gap;
}
</style>
